import { Slot } from "@radix-ui/react-slot";
import { type ComponentPropsWithoutRef, Fragment, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { cn } from "../lib/utils";
import { Icon, type IconSymbol } from "./icon";

type BreadcrumbDataItem = {
  label: string;
  icon?: IconSymbol;
  to?: string;
  isCurrent?: boolean;
};

interface CustomBreadcrumbProps extends React.HTMLAttributes<HTMLElement> {
  items: BreadcrumbDataItem[];
  separator?: React.ReactNode;
}

const BreadcrumbWrapper = (
  props: ComponentPropsWithoutRef<"nav"> & {
    separator?: ReactNode;
    ref?: React.Ref<HTMLElement>;
  },
) => {
  const { ref, ...rest } = props;
  return <nav ref={ref} aria-label="breadcrumb" {...rest} />;
};
BreadcrumbWrapper.displayName = "BreadcrumbWrapper";

const BreadcrumbList = (
  props: ComponentPropsWithoutRef<"ol"> & { ref?: React.Ref<HTMLOListElement> },
) => {
  const { className, ref, ...rest } = props;
  return (
    <ol
      ref={ref}
      className={cn(
        "flex flex-wrap items-center gap-1.5 break-words text-sm text-muted-foreground sm:gap-2.5",
        className,
      )}
      {...rest}
    />
  );
};
BreadcrumbList.displayName = "BreadcrumbList";

const BreadcrumbItem = (
  props: ComponentPropsWithoutRef<"li"> & { ref?: React.Ref<HTMLLIElement> },
) => {
  const { className, ref, ...rest } = props;
  return (
    <li
      ref={ref}
      className={cn(
        "inline-flex items-center gap-2.5 text-gray-700 text-caption ",
        className,
      )}
      {...rest}
    />
  );
};
BreadcrumbItem.displayName = "BreadcrumbItem";

const BreadcrumbLink = (
  props: ComponentPropsWithoutRef<typeof Link> & {
    asChild?: boolean;
    ref?: React.Ref<HTMLAnchorElement>;
  },
) => {
  const { asChild, className, ref, ...rest } = props;
  const Comp = asChild ? Slot : Link;
  return (
    <Comp
      ref={ref}
      className={cn("transition-colors hover:text-foreground", className)}
      {...rest}
    />
  );
};
BreadcrumbLink.displayName = "BreadcrumbLink";

const BreadcrumbPage = (
  props: ComponentPropsWithoutRef<"span"> & {
    ref?: React.Ref<HTMLSpanElement>;
  },
) => {
  const { className, ref, ...rest } = props;
  return (
    <span
      ref={ref}
      role="link"
      aria-disabled="true"
      aria-current="page"
      tabIndex={0}
      className={cn("font-normal text-foreground", className)}
      {...rest}
    />
  );
};
BreadcrumbPage.displayName = "BreadcrumbPage";
const DEFAULT_SEPARATOR = (
  <Icon
    icon="chevron-right"
    className="h-4 w-4 text-gray-400 !flex !items-center"
  />
);

export const Breadcrumb = ({
  items,
  separator = DEFAULT_SEPARATOR,
  ...props
}: CustomBreadcrumbProps) => {
  return (
    <BreadcrumbWrapper {...props}>
      <BreadcrumbList>
        {items.map(({ label, icon, isCurrent, to }, index) => {
          const isLastItem = index === items.length - 1;

          return (
            <Fragment
              key={`breadcrumb-${label}-${
                // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                index
              }`}
            >
              <BreadcrumbItem className="hover:cursor-pointer">
                {icon && (
                  <Icon icon={icon} className="mr-1.5 h-4 w-4 shrink-0" />
                )}
                <BreadcrumbLink
                  to={to || "#"}
                  className={cn(
                    "hover:text-pink-600",
                    isCurrent ? "font-title" : "font-base",
                  )}
                >
                  {label}
                </BreadcrumbLink>
              </BreadcrumbItem>

              {!isLastItem && (
                <span className="flex items-center">{separator}</span>
              )}
            </Fragment>
          );
        })}
      </BreadcrumbList>
    </BreadcrumbWrapper>
  );
};
